import type { AnyRouter } from "@tanstack/react-router";

import { authExchange } from "@urql/exchange-auth";
import { cacheExchange } from "@urql/exchange-graphcache";
import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
import { toast } from "sonner";
import {
  Provider as UrqlProvider,
  fetchExchange,
  createClient,
  cacheExchange as simpleCacheExchange,
} from "urql";

import { adminDeleteUser } from "@/features/users/graphql/cache-updates/admin-delete-user";

import { useAuth } from "./auth-provider";

type GraphQLProviderProps = Readonly<{
  children: React.ReactNode;
  router: AnyRouter;
}>;

export function GraphqlProvider({ children }: GraphQLProviderProps) {
  const { isAuthenticated, getAccessTokenSilently, logout, loginWithRedirect } = useAuth();

  const client = useMemo(() => {
    if (!isAuthenticated) {
      return createClient({
        preferGetMethod: false,
        url: import.meta.env.VITE_GRAPHQL_HTTP_ENDPOINT,
        exchanges: [simpleCacheExchange, fetchExchange],
      });
    }

    return createClient({
      preferGetMethod: false,
      url: import.meta.env.VITE_GRAPHQL_HTTP_ENDPOINT,
      exchanges: [
        cacheExchange({
          keys: {
            SystemPayload: () => null,
          },
          updates: {
            Mutation: {
              adminDeleteUser: adminDeleteUser,
            },
          },
        }),
        authExchange(async (utilities) => {
          const accessToken = await getAccessTokenSilently().catch((error) => {
            if (error.message === "Consent required") {
              loginWithRedirect();
            }
            toast.error("Unable to refresh access token!", {
              description: JSON.stringify(error),
            });
          });

          const now = new Date();
          // assume token is empty and expires in 5 minutes
          let token = "";
          let expiresAt = now.valueOf() + 300_000;

          if (accessToken) {
            token = accessToken;
            const payload = jwtDecode(accessToken);
            if (payload.exp) {
              expiresAt = payload.exp * 1_000;
            }
          } else {
            toast.warning("Access token not returned from Auth0");
          }
          return {
            addAuthToOperation(operation) {
              return token
                ? utilities.appendHeaders(operation, {
                    Authorization: `Bearer ${token}`,
                    "x-copper-role": "global_admin",
                  })
                : operation;
            },
            didAuthError(error) {
              if (error.response) {
                return error.response?.status === 401;
              }

              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                return error.graphQLErrors.some((item) => item.extensions?.code === "A0001");
              }

              if (error.networkError) {
                return true;
              }

              return false;
            },
            async refreshAuth() {
              console.warn("refreshing auth");
              try {
                token = await getAccessTokenSilently();
                const payload = jwtDecode(token);
                if (payload.exp) {
                  expiresAt = payload.exp * 1_000;
                }
              } catch (error) {
                if (error instanceof Error && error.message === "Consent required") {
                  loginWithRedirect();
                } else {
                  console.error("getAccessTokenSilently threw an error", { error });
                }
              }
            },
            willAuthError() {
              return !token || expiresAt < Date.now();
            },
          };
        }),
        fetchExchange,
      ],
    });
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  return <UrqlProvider value={client}>{children}</UrqlProvider>;
}
